import { useState, useEffect, useRef } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Mic, MicOff, Volume2, Timer, Play, Square, RotateCcw, TrendingUp } from 'lucide-react';

const LEVELS = [
  { label: 'Silent', max: 20, color: 'bg-emerald-500', textColor: 'text-emerald-600', emoji: 'Quiet work time' },
  { label: 'Whisper', max: 40, color: 'bg-green-500', textColor: 'text-green-600', emoji: 'Nice and quiet' },
  { label: 'Talk', max: 60, color: 'bg-amber-500', textColor: 'text-amber-600', emoji: 'Indoor voices' },
  { label: 'Loud', max: 80, color: 'bg-orange-500', textColor: 'text-orange-600', emoji: 'Getting loud' },
  { label: 'Too Loud', max: 100, color: 'bg-rose-500', textColor: 'text-rose-600', emoji: 'Way too loud!' },
];

const AVG_WINDOW = 30; // Rolling average over last 30 samples (~2 seconds)

export const NoiseMeterPage = () => {
  const [active, setActive] = useState(false);
  const [volume, setVolume] = useState(0);
  const [average, setAverage] = useState(0);
  const [threshold, setThreshold] = useState(60);
  const samplesRef = useRef([]);
  const analyserRef = useRef(null);
  const streamRef = useRef(null);
  const animRef = useRef(null);

  // Timer state
  const [timerMinutes, setTimerMinutes] = useState('');
  const [timerSeconds, setTimerSeconds] = useState('');
  const [timerRunning, setTimerRunning] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const timerRef = useRef(null);

  const startListening = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.8;
      source.connect(analyser);
      analyserRef.current = { analyser, audioCtx };
      samplesRef.current = [];
      setActive(true);

      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      const tick = () => {
        analyser.getByteFrequencyData(dataArray);
        const avg = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
        const normalized = Math.min(100, Math.round((avg / 128) * 100));
        setVolume(normalized);

        // Rolling average
        samplesRef.current.push(normalized);
        if (samplesRef.current.length > AVG_WINDOW) samplesRef.current.shift();
        const rollingAvg = Math.round(samplesRef.current.reduce((a, b) => a + b, 0) / samplesRef.current.length);
        setAverage(rollingAvg);

        animRef.current = requestAnimationFrame(tick);
      };
      tick();
    } catch (error) {
      console.error('Microphone access denied:', error);
    }
  };

  const stopListening = () => {
    if (animRef.current) cancelAnimationFrame(animRef.current);
    if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    if (analyserRef.current?.audioCtx) analyserRef.current.audioCtx.close();
    setActive(false);
    setVolume(0);
    setAverage(0);
    samplesRef.current = [];
  };

  const resetAverage = () => { samplesRef.current = []; setAverage(0); };

  // Timer logic
  const startTimer = () => {
    const mins = parseInt(timerMinutes) || 0;
    const secs = parseInt(timerSeconds) || 0;
    const total = mins * 60 + secs;
    if (total <= 0) return;
    setTimeLeft(total);
    setTimerRunning(true);
  };

  const stopTimer = () => {
    setTimerRunning(false);
    if (timerRef.current) clearInterval(timerRef.current);
  };

  const resetTimer = () => { stopTimer(); setTimeLeft(0); };

  useEffect(() => {
    if (!timerRunning) return;
    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) { setTimerRunning(false); stopListening(); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [timerRunning]);

  useEffect(() => { return () => { stopListening(); stopTimer(); }; }, []);

  const formatTime = (s) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;
  const currentLevel = LEVELS.find(l => volume <= l.max) || LEVELS[LEVELS.length - 1];
  const avgLevel = LEVELS.find(l => average <= l.max) || LEVELS[LEVELS.length - 1];
  const aboveThreshold = active && average > threshold;

  const breadcrumb = [
    { label: 'Teach Now', path: '/app/live-mode' },
    { label: 'Noise Meter' }
  ];

  return (
    <Layout breadcrumb={breadcrumb}>
      <div className="max-w-md mx-auto space-y-5" data-testid="noise-meter-page">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-teal-50 mb-4">
            <Volume2 className="w-7 h-7 text-teal-600" />
          </div>
          <h1 className="text-2xl font-heading font-semibold text-slate-900">Noise Meter</h1>
          <p className="text-slate-500 mt-1 text-sm">Monitor classroom noise — keep the average acceptable</p>
        </div>

        {/* Timer Card */}
        <Card className="bg-white border border-slate-200 rounded-xl shadow-soft">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Timer className="w-4 h-4 text-slate-500" />
              <h2 className="text-sm font-heading font-semibold text-slate-700">Timer</h2>
            </div>
            {timeLeft > 0 || timerRunning ? (
              <div className="flex items-center justify-between">
                <div className={`text-3xl font-heading font-bold ${timeLeft <= 10 && timeLeft > 0 ? 'text-rose-500 animate-pulse' : 'text-slate-900'}`} data-testid="timer-display">
                  {formatTime(timeLeft)}
                </div>
                <div className="flex gap-2">
                  {timerRunning ? (
                    <Button variant="outline" onClick={stopTimer} className="border-slate-200 rounded-lg h-9 px-3 text-sm" data-testid="stop-timer-btn"><Square className="w-3.5 h-3.5 mr-1" />Stop</Button>
                  ) : (
                    <Button onClick={() => setTimerRunning(true)} className="bg-teal-600 hover:bg-teal-700 text-white rounded-lg h-9 px-3 text-sm" data-testid="resume-timer-btn"><Play className="w-3.5 h-3.5 mr-1" />Resume</Button>
                  )}
                  <Button variant="outline" onClick={resetTimer} className="border-slate-200 rounded-lg h-9 px-3 text-sm" data-testid="reset-timer-btn"><RotateCcw className="w-3.5 h-3.5" /></Button>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1">
                  <Input type="number" min="0" max="99" placeholder="min" value={timerMinutes} onChange={(e) => setTimerMinutes(e.target.value)} className="w-16 h-9 text-center border-slate-200 rounded-lg text-sm" data-testid="timer-minutes-input" />
                  <span className="text-slate-400 font-bold">:</span>
                  <Input type="number" min="0" max="59" placeholder="sec" value={timerSeconds} onChange={(e) => setTimerSeconds(e.target.value)} className="w-16 h-9 text-center border-slate-200 rounded-lg text-sm" data-testid="timer-seconds-input" />
                </div>
                <Button onClick={startTimer} className="bg-teal-600 hover:bg-teal-700 text-white rounded-lg h-9 px-4 text-sm" data-testid="start-timer-btn"><Play className="w-3.5 h-3.5 mr-1" />Start</Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Noise Meter Card */}
        <Card className={`bg-white border rounded-xl shadow-soft transition-all ${aboveThreshold ? 'border-rose-300 ring-2 ring-rose-100' : 'border-slate-200'}`}>
          <CardContent className="p-6">
            {/* Main display */}
            <div className="text-center mb-6">
              <div className={`text-7xl font-heading font-bold transition-colors duration-300 ${currentLevel.textColor}`} data-testid="volume-display">
                {active ? volume : '--'}
              </div>
              <p className={`text-lg font-medium mt-2 transition-colors duration-300 ${active ? currentLevel.textColor : 'text-slate-400'}`} data-testid="volume-label">
                {active ? currentLevel.emoji : 'Tap to start'}
              </p>
            </div>

            {/* Volume bars */}
            <div className="flex items-end justify-center gap-1.5 h-32 mb-6">
              {Array.from({ length: 20 }, (_, i) => {
                const t = (i + 1) * 5;
                const isActive = active && volume >= t;
                const level = LEVELS.find(l => t <= l.max) || LEVELS[LEVELS.length - 1];
                return (
                  <div
                    key={i}
                    className={`w-3 rounded-t-sm transition-all duration-150 ${isActive ? level.color : 'bg-slate-100'}`}
                    style={{ height: `${20 + i * 5}%` }}
                    data-testid={`bar-${i}`}
                  />
                );
              })}
            </div>

            {/* Average display */}
            {active && (
              <div className={`rounded-xl p-4 mb-6 text-center transition-all ${aboveThreshold ? 'bg-rose-50 border border-rose-200' : 'bg-slate-50 border border-slate-100'}`} data-testid="average-display">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <TrendingUp className={`w-4 h-4 ${aboveThreshold ? 'text-rose-500' : 'text-slate-500'}`} />
                  <span className={`text-xs font-medium ${aboveThreshold ? 'text-rose-600' : 'text-slate-500'}`}>Rolling Average</span>
                </div>
                <span className={`text-4xl font-heading font-bold ${avgLevel.textColor}`} data-testid="average-value">{average}</span>
                <p className={`text-xs mt-1 ${aboveThreshold ? 'text-rose-500 font-medium' : 'text-slate-400'}`}>
                  {aboveThreshold ? `Above threshold (${threshold})` : `Threshold: ${threshold}`}
                </p>
                <button onClick={resetAverage} className="text-xs text-teal-600 hover:text-teal-700 underline mt-1" data-testid="reset-average-btn">Reset average</button>
              </div>
            )}

            {/* Threshold slider */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-slate-500">Acceptable noise level</span>
                <span className="text-xs font-semibold text-slate-700">{threshold}</span>
              </div>
              <input
                type="range" min="10" max="90" value={threshold}
                onChange={(e) => setThreshold(parseInt(e.target.value))}
                className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
                data-testid="threshold-slider"
              />
              <div className="flex justify-between text-[10px] text-slate-400 mt-1">
                <span>Silent</span>
                <span>Loud</span>
              </div>
            </div>

            {/* Level indicators */}
            <div className="flex justify-between mb-6 px-2">
              {LEVELS.map(level => (
                <div key={level.label} className="text-center">
                  <div className={`w-3 h-3 rounded-full mx-auto mb-1 ${level.color}`} />
                  <span className="text-[10px] text-slate-500">{level.label}</span>
                </div>
              ))}
            </div>

            {/* Controls */}
            <div className="flex gap-3 justify-center">
              <Button
                onClick={active ? stopListening : startListening}
                className={`rounded-xl h-12 px-8 text-base ${active ? 'bg-rose-500 hover:bg-rose-600' : 'bg-teal-600 hover:bg-teal-700'} text-white`}
                data-testid="toggle-mic-btn"
              >
                {active ? <><MicOff className="w-5 h-5 mr-2" />Stop</> : <><Mic className="w-5 h-5 mr-2" />Start</>}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};
